import { db } from '../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { collection, addDoc } from 'firebase/firestore';

export interface SubscriptionHistoryEntry {
  action: 'granted' | 'extended' | 'revoked';
  months: number;
  grantedBy: string; // admin/trainer ID
  grantedByName?: string; // admin/trainer name
  timestamp: any; // Firestore timestamp
  previousEndDate?: any;
  newEndDate?: any;
  previousModules?: number[];
  newModules?: number[];
}

/**
 * Grant or extend subscription for a user
 * @param userId - User ID to grant subscription to
 * @param months - Number of months to grant (1-12)
 * @param grantedBy - Admin/Trainer user ID who is granting
 * @param grantedByName - Admin/Trainer display name
 * @returns Promise<void>
 */
export async function grantSubscription(
  userId: string,
  months: number,
  grantedBy: string,
  grantedByName?: string
): Promise<void> {
  if (months < 1 || months > 12) {
    throw new Error('Months must be between 1 and 12');
  }

  try {
    // Get current user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const now = Timestamp.now();
    
    // Calculate plan name
    const planName = months === 1 ? '1month' : 
                     months === 3 ? '3months' : 
                     months === 6 ? '6months' : 
                     '12months';

    // Get existing subscription or create new
    const existingSubscription = userData.subscription || {};
    const existingEndDate = existingSubscription.endDate;
    const existingStartDate = existingSubscription.startDate || now;
    
    // Calculate new end date: if existing subscription, extend from existing end date or now
    let newEndDate: Timestamp;
    if (existingSubscription.active && existingEndDate) {
      // Extend from existing end date
      const existingEnd = existingEndDate.toDate ? existingEndDate.toDate() : new Date(existingEndDate.seconds * 1000);
      const extendedDate = new Date(existingEnd);
      extendedDate.setMonth(extendedDate.getMonth() + months);
      newEndDate = Timestamp.fromDate(extendedDate);
    } else {
      // New subscription: start from now
      const newDate = new Date();
      newDate.setMonth(newDate.getMonth() + months);
      newEndDate = Timestamp.fromDate(newDate);
    }

    // Calculate total months for modules (existing + new)
    const existingMonths = existingSubscription.unlockedModules?.length || 0;
    const totalMonths = existingMonths + months;
    const unlockedModules = Array.from({ length: totalMonths }, (_, i) => i + 1);

    // Prepare history entry
    // Use Timestamp.now() instead of serverTimestamp() because it's going into an array
    const historyEntry: SubscriptionHistoryEntry = {
      action: existingSubscription.active ? 'extended' : 'granted',
      months,
      grantedBy,
      grantedByName: grantedByName || 'Admin',
      timestamp: now, // Use Timestamp.now() instead of serverTimestamp() for arrays
      previousEndDate: existingEndDate || null, // Use null instead of undefined
      newEndDate,
      previousModules: existingSubscription.unlockedModules || [],
      newModules: unlockedModules
    };

    // Update user document
    // Ensure no undefined values are passed - clean the history entry
    const cleanedHistoryEntry: any = {
      action: historyEntry.action,
      months: historyEntry.months,
      grantedBy: historyEntry.grantedBy,
      grantedByName: historyEntry.grantedByName || 'Admin',
      timestamp: historyEntry.timestamp,
      previousEndDate: historyEntry.previousEndDate ?? null,
      newEndDate: historyEntry.newEndDate,
      previousModules: historyEntry.previousModules || [],
      newModules: historyEntry.newModules || []
    };

    const subscriptionUpdate: any = {
      subscription: {
        active: true,
        plan: planName,
        startDate: existingStartDate || now,
        endDate: newEndDate,
        unlockedModules,
        history: [...(existingSubscription.history || []), cleanedHistoryEntry]
      }
    };

    await updateDoc(userRef, subscriptionUpdate);

    // Create purchase record in purchases collection
    const purchaseData = {
      userId,
      userEmail: userData.email || '',
      userName: userData.displayName || userData.name || '',
      planName: `${months} month${months > 1 ? 's' : ''} (Admin Granted)`,
      planMonths: months,
      amount: 0, // Admin granted = free
      currency: 'usd',
      stripeSessionId: `admin-granted-${Date.now()}-${grantedBy}`, // Unique identifier
      createdAt: serverTimestamp(),
      expiresAt: newEndDate,
      isActive: true,
      adminGranted: true,
      grantedBy,
      grantedByName: grantedByName || 'Admin',
      stripePaymentIntentId: null // Explicitly set to null if not provided
    };

    await addDoc(collection(db, 'purchases'), purchaseData);

    console.log(`Subscription granted: ${months} months to user ${userId} by ${grantedBy}`);
  } catch (error) {
    console.error('Error granting subscription:', error);
    throw error;
  }
}

/**
 * Grant default admin/trainer subscription (12 months, no expiry)
 * This is automatically called for admin/trainer users
 * @param userId - User ID to grant subscription to
 * @returns Promise<void>
 */
export async function grantAdminTrainerSubscription(userId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const role = userData.role;
    
    // Only grant for admin or trainer
    if (role !== 'admin' && role !== 'trainer') {
      return; // Not an admin/trainer, skip
    }

    const existingSubscription = userData.subscription || {};
    
    // Only grant if they don't already have an active subscription
    if (existingSubscription.active) {
      return; // Already has active subscription
    }

    const now = Timestamp.now();
    // Set expiry to 100 years from now (effectively no expiry)
    const farFutureDate = new Date();
    farFutureDate.setFullYear(farFutureDate.getFullYear() + 100);
    const noExpiryDate = Timestamp.fromDate(farFutureDate);

    // 12 months = all modules unlocked (1-12)
    const unlockedModules = Array.from({ length: 12 }, (_, i) => i + 1);

    // Prepare history entry
    // Use Timestamp.now() instead of serverTimestamp() because it's going into an array
    const historyEntry: SubscriptionHistoryEntry = {
      action: 'granted',
      months: 12,
      grantedBy: 'system',
      grantedByName: 'System (Admin/Trainer Default)',
      timestamp: now, // Use Timestamp.now() instead of serverTimestamp() for arrays
      previousEndDate: null,
      newEndDate: noExpiryDate,
      previousModules: [],
      newModules: unlockedModules
    };

    // Update user document
    // Ensure no undefined values are passed - clean the history entry
    const cleanedHistoryEntry: any = {
      action: historyEntry.action,
      months: historyEntry.months,
      grantedBy: historyEntry.grantedBy,
      grantedByName: historyEntry.grantedByName || 'System',
      timestamp: historyEntry.timestamp,
      previousEndDate: historyEntry.previousEndDate ?? null,
      newEndDate: historyEntry.newEndDate,
      previousModules: historyEntry.previousModules || [],
      newModules: historyEntry.newModules || []
    };

    const subscriptionUpdate: any = {
      subscription: {
        active: true,
        plan: '12months',
        startDate: now,
        endDate: noExpiryDate,
        unlockedModules,
        history: [cleanedHistoryEntry],
        isAdminTrainer: true // Flag to identify admin/trainer subscriptions
      }
    };

    await updateDoc(userRef, subscriptionUpdate);

    // Create purchase record
    const purchaseData = {
      userId,
      userEmail: userData.email || '',
      userName: userData.displayName || userData.name || '',
      planName: '12 months (Admin/Trainer Default)',
      planMonths: 12,
      amount: 0,
      currency: 'usd',
      stripeSessionId: `admin-trainer-default-${Date.now()}-${userId}`,
      createdAt: serverTimestamp(),
      expiresAt: noExpiryDate,
      isActive: true,
      adminGranted: true,
      grantedBy: 'system',
      grantedByName: 'System (Admin/Trainer Default)'
    };

    await addDoc(collection(db, 'purchases'), purchaseData);

    console.log(`Admin/Trainer subscription granted to user ${userId}`);
  } catch (error) {
    console.error('Error granting admin/trainer subscription:', error);
    // Don't throw - this is a background operation
  }
}

